// src/net/leaderboard.ts — the CLIENT's leaderboard API. Pure `fetch`; no Anthropic SDK; NO key.
//
// Mirrors boardClient.ts's resilience doctrine: the backend MAY expose a leaderboard
// (POST /scores to submit, GET /scores?scope=...&topic=... to read top-N). Until that route lands
// on the Render Web Service, EVERY call transparently falls back to a localStorage-backed local
// leaderboard so the M3 retention UX is fully functional offline/now, and auto-upgrades to the live
// global board the moment the backend route returns 2xx. A 404/network/timeout never dead-ends the UI.
//
// Scopes:
//   'global' — all topics, all players (the headline board)
//   'topic'  — restricted to one topicNormalized slug (per-topic bests)
//   'daily'  — restricted to today's UTC daily run (a fresh competition each day)
//
// The remembered player name lives in localStorage (one prompt, reused) — never a secret.

import type {
  LeaderboardScope,
  ScoreEntry,
  SubmitScoreInput,
  TopScoresQuery,
  LeaderboardOptions,
} from './types';

const DEFAULT_TIMEOUT_MS = 5_000;
const LOCAL_KEY = 'trivia.leaderboard.v1';
const NAME_KEY = 'trivia.playerName.v1';
const MAX_LOCAL_PER_BUCKET = 50;

function apiBase(): string {
  const raw = import.meta.env?.VITE_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

// --- remembered player name -------------------------------------------------

export function getRememberedName(): string {
  try {
    return window.localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function rememberName(name: string): void {
  const clean = sanitizeName(name);
  if (!clean) return;
  try {
    window.localStorage.setItem(NAME_KEY, clean);
  } catch {
    /* best-effort */
  }
}

/** Trim, collapse whitespace, strip control chars, cap at 16 — safe to render and to send. */
export function sanitizeName(raw: string): string {
  // strip C0/C1 control chars by code point (avoids a control-char literal in the regex source,
  // which eslint's no-control-regex flags), then collapse whitespace + cap length.
  let out = '';
  for (const ch of raw) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) continue;
    out += ch;
  }
  return out.replace(/\s+/g, ' ').trim().slice(0, 16);
}

// --- HTTP helpers (timeout + external abort) --------------------------------

async function withTimeout(
  fn: (signal: AbortSignal) => Promise<Response>,
  opts: LeaderboardOptions,
): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctrl = new AbortController();
  const onAbort = (): void => ctrl.abort();
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fn(ctrl.signal);
  } finally {
    clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
  }
}

// --- local fallback store ---------------------------------------------------

interface LocalStore {
  // bucketKey -> entries (bucketKey = scope or "topic:<slug>" or "daily:<utcDay>")
  buckets: Record<string, ScoreEntry[]>;
}

function readLocal(): LocalStore {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    if (!raw) return { buckets: {} };
    const o = JSON.parse(raw) as Partial<LocalStore>;
    if (!o || typeof o !== 'object' || typeof o.buckets !== 'object' || o.buckets === null) {
      return { buckets: {} };
    }
    return { buckets: o.buckets as Record<string, ScoreEntry[]> };
  } catch {
    return { buckets: {} };
  }
}

function writeLocal(store: LocalStore): void {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(store));
  } catch {
    /* best-effort */
  }
}

function bucketKeyFor(scope: LeaderboardScope, topic: string | undefined, day: string, week: string): string {
  if (scope === 'topic') return `topic:${topic ?? ''}`;
  if (scope === 'daily') return `daily:${day}`;
  if (scope === 'weekly') return `weekly:${week}`;
  return 'global';
}

function utcDay(): string {
  return new Date().toISOString().slice(0, 10);
}

/** UTC Monday (YYYY-MM-DD) of this week — the P2.1 league-week key. Matches the backend's utcWeekKey. */
function utcWeek(): string {
  const d = new Date();
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday … 6 = Sunday
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow));
  return monday.toISOString().slice(0, 10);
}

/** Insert a score into a local bucket, keep sorted desc + capped. Returns the new top slice. */
function insertLocal(bucketKey: string, entry: ScoreEntry): ScoreEntry[] {
  const store = readLocal();
  const list = store.buckets[bucketKey] ?? [];
  list.push(entry);
  list.sort((a, b) => b.score - a.score || a.at - b.at);
  store.buckets[bucketKey] = list.slice(0, MAX_LOCAL_PER_BUCKET);
  writeLocal(store);
  return store.buckets[bucketKey];
}

function readLocalTop(bucketKey: string, limit: number): ScoreEntry[] {
  const store = readLocal();
  return (store.buckets[bucketKey] ?? []).slice(0, limit);
}

// --- public API -------------------------------------------------------------

/**
 * Submit a score. Tries the backend first; on ANY non-2xx / network / timeout it records to the
 * local fallback so the board is never empty. Returns `{ live }` so the UI can note "submitted to
 * the global board" vs "saved locally (offline)". Submitting also always mirrors to the LOCAL store
 * for the topic/daily/global buckets so a player's own runs are visible immediately regardless.
 */
export async function submitScore(
  input: SubmitScoreInput,
  opts: LeaderboardOptions = {},
): Promise<{ live: boolean; rank?: number }> {
  const name = sanitizeName(input.name) || 'Player';
  rememberName(name);
  const at = Date.now();
  const day = utcDay();
  const entry: ScoreEntry = {
    name,
    score: Math.max(0, Math.floor(input.score)),
    topicLabel: input.topicLabel,
    topicNormalized: input.topicNormalized,
    at,
    isYou: true,
  };

  // Always mirror locally (own-runs always visible, and the offline board stays useful). Every run
  // also lands on the current weekly league (P2.1) — the backend fans out the same way server-side.
  insertLocal('global', entry);
  insertLocal(`weekly:${utcWeek()}`, entry);
  if (input.topicNormalized) insertLocal(`topic:${input.topicNormalized}`, entry);
  if (input.isDaily) insertLocal(`daily:${day}`, entry);

  const base = apiBase();
  try {
    const res = await withTimeout(
      (signal) =>
        fetch(`${base}/scores`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            name,
            score: entry.score,
            topic: input.topicNormalized,
            topicLabel: input.topicLabel,
            daily: !!input.isDaily,
            seed: input.seed,
          }),
          signal,
        }),
      opts,
    );
    if (res.ok) {
      let rank: number | undefined;
      try {
        const body = (await res.json()) as { rank?: number };
        if (typeof body?.rank === 'number') rank = body.rank;
      } catch {
        /* body optional */
      }
      return { live: true, rank };
    }
    return { live: false };
  } catch {
    return { live: false };
  }
}

/**
 * Read the top-N for a scope. Tries the backend; falls back to the local store on failure. Marks
 * `isYou` on rows whose name matches the remembered name so the UI can highlight the player.
 */
export async function getTopScores(
  query: TopScoresQuery,
  opts: LeaderboardOptions = {},
): Promise<{ entries: ScoreEntry[]; live: boolean }> {
  const limit = query.limit ?? 10;
  const day = utcDay();
  const bucketKey = bucketKeyFor(query.scope, query.topicNormalized, day, utcWeek());
  const you = getRememberedName();

  const base = apiBase();
  try {
    const params = new URLSearchParams();
    params.set('scope', query.scope);
    params.set('limit', String(limit));
    if (query.scope === 'topic' && query.topicNormalized) params.set('topic', query.topicNormalized);
    const res = await withTimeout(
      (signal) => fetch(`${base}/scores?${params.toString()}`, { method: 'GET', headers: { accept: 'application/json' }, signal }),
      opts,
    );
    if (res.ok) {
      const data = (await res.json()) as { entries?: unknown };
      const entries = normalizeEntries(data?.entries, you);
      // An empty daily/weekly board is still a LIVE answer (a fresh competition with no entries yet),
      // not a signal to fall back to the local store.
      if (entries.length > 0 || query.scope === 'daily' || query.scope === 'weekly') {
        // Merge in any local-only rows the player just set this session that the server hasn't
        // propagated yet (so their own fresh score is always visible alongside the live board).
        return { entries: mergeYouRows(entries, readLocalTop(bucketKey, limit), you, limit), live: true };
      }
    }
  } catch {
    /* fall through to local */
  }

  const local = readLocalTop(bucketKey, limit).map((e) => ({ ...e, isYou: !!you && e.name === you }));
  return { entries: local, live: false };
}

/** Coerce an unknown server `entries` payload into safe ScoreEntry[]. */
function normalizeEntries(raw: unknown, you: string): ScoreEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ScoreEntry[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const name = sanitizeName(typeof o.name === 'string' ? o.name : '') || 'Player';
    const score = typeof o.score === 'number' && Number.isFinite(o.score) ? Math.max(0, Math.floor(o.score)) : 0;
    out.push({
      name,
      score,
      topicLabel: typeof o.topicLabel === 'string' ? o.topicLabel : undefined,
      topicNormalized: typeof o.topic === 'string' ? o.topic : typeof o.topicNormalized === 'string' ? o.topicNormalized : undefined,
      at: typeof o.at === 'number' ? o.at : 0,
      isYou: !!you && name === you,
    });
  }
  return out;
}

/**
 * Ensure the player's own fresh local rows appear even if the server list hasn't propagated them
 * yet. Dedupe by name+score (NOT by `at`, which differs between the local mirror and the server's
 * own timestamp) so a just-submitted score never shows twice once the server echoes it back.
 */
function mergeYouRows(server: ScoreEntry[], local: ScoreEntry[], you: string, limit: number): ScoreEntry[] {
  if (!you) return server.slice(0, limit);
  const serverYouScores = new Set(server.filter((e) => e.name === you).map((e) => e.score));
  const merged = [...server];
  for (const l of local) {
    if (l.name !== you) continue;
    if (serverYouScores.has(l.score)) continue; // server already has this score for you
    merged.push({ ...l, isYou: true });
  }
  merged.sort((a, b) => b.score - a.score || a.at - b.at);
  return merged.slice(0, limit);
}
